<!--
  @component ApprovalsIO — P2 effecting lens (u4 human-in-the-loop).
  chain:   ctx.deployment.dispatch("approve"|"deny"|"resolve", …)
           → this → ctx.approvals
  reads:   stream-filtered pending requests + decided events
  effects: approve / deny / resolve dispatched through the command
           port. Unwired ⇒ NotWiredError (FACE-LEDGER).
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { APPROVALS_IO_KEY } from "./deployment-io.js";
  import type { ApprovalsIOContext } from "./deployment-io.js";

  export { APPROVALS_IO_KEY };
  export type { ApprovalsIOContext };

  export function useApprovalsIO(): ApprovalsIOContext {
    const ctx = getContext<ApprovalsIOContext | undefined>(APPROVALS_IO_KEY);
    if (!ctx) {
      throw new Error(
        "useApprovalsIO() called outside of an ApprovalsIO tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastError: Error | null = $state(null);

  async function decide(
    op: "approve" | "deny" | "resolve",
    payload: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    inFlight = true;
    lastError = null;
    try {
      return await parent.dispatch(op, payload);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally {
      inFlight = false;
    }
  }

  const ctx: ApprovalsIOContext = {
    get inFlight() {
      return inFlight;
    },
    get lastError() {
      return lastError;
    },
    get pending() {
      return parent.stream.filter(
        (e) => e.op === "approval-requested" || e.op === "approval.pending",
      );
    },
    get decisions() {
      return parent.stream.filter(
        (e) =>
          e.op === "approval-decided" ||
          e.op === "approve" ||
          e.op === "deny",
      );
    },
    approve: (approvalId, note) =>
      decide("approve", note === undefined ? { approvalId } : { approvalId, note }),
    deny: (approvalId, reason) => decide("deny", { approvalId, reason }),
    resolve: (approvalId) => decide("resolve", { approvalId }),
  } satisfies ApprovalsIOContext;

  setContext(APPROVALS_IO_KEY, ctx);
</script>

{@render children?.()}
